import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import { isValidPostalCode } from "@/lib/validations/postalCode.validation";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { i18nMessage } from "@/lib/i18nMessage";

export const customCotationSingleDeliveryFormSchema = z.object({
  singleDelivery: z.literal(true),
  deliveryLastName: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.lastNameRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.lastNameRequired")),
  deliveryFirstName: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.firstNameRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.firstNameRequired")),
  deliveryCompany: z.string().optional(),
  deliveryPhone: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.phoneRequired"),
    })
    .refine((mobile) => isValidPhoneNumber(mobile), {
      message: i18nMessage("validation:invalidPhoneNumber"),
    }),
  deliveryEmail: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.emailRequired"),
    })
    .trim()
    .email({
      message: i18nMessage("validation:invalidEmail"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.emailRequired")),
  deliveryStreet: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.streetRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.streetRequired")),
  deliveryAdditionalInfo: z.string().optional(),
  deliveryAdditionalInfo2: z.string().optional(),
  deliveryPostalCode: z.string().optional(),
  deliveryCity: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.cityRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.cityRequired")),
  countryCode: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.countryCodeRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.countryCodeRequired")),
  stateOrProvinceCode: z.string().optional(),
  customId: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.customIdRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.customIdRequired")),
});

export const customCotationMultipleDeliveryFormSchema = z.object({
  singleDelivery: z.literal(false),
  otherNumberOfExpedition: z
    .number({
      coerce: true,
      required_error: i18nMessage("shared:forms.customQuotationOther.numberOfDeliveriesRequired"),
      message: i18nMessage("shared:forms.customQuotationOther.numberOfDeliveriesRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.numberOfDeliveriesRequired")),
});

export const customCotationManuelPickupFormSchema = z.object({
  manualPickup: z.literal(true),
  pickupLastName: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.lastNameRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotationOther.lastNameRequired"),
    }),
  pickupFirstName: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.firstNameRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotationOther.firstNameRequired"),
    }),
  pickupCompany: z.string().optional(),
  pickupPhone: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.phoneRequired"),
    })
    .refine((mobile) => isValidPhoneNumber(mobile), {
      message: i18nMessage("validation:invalidPhoneNumber"),
    }),
  pickupStreet: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.streetRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotationOther.streetRequired"),
    }),
  pickupPostalCode: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.postalCodeRequired"),
    })
    .refine((postalCode) => isValidPostalCode(postalCode), {
      message: i18nMessage("validation:invalidPostalCode"),
    }),
  pickupCity: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.cityRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotationOther.cityRequired"),
    }),
  pickupCountry: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.pickupCountryRequired"),
    })
    .min(1, {
      message: i18nMessage("shared:forms.customQuotationOther.pickupCountryRequired"),
    }),
});

export const customCotationPickupLocationIdFormSchema = z.object({
  manualPickup: z.literal(false),
  pickupLocationId: z
    .string({
      message: i18nMessage("shared:forms.customQuotationOther.pickupLocationRequired"),
    })
    .min(1, i18nMessage("shared:forms.customQuotationOther.pickupLocationRequired")),
});

export const CustomCotationsOtherFormSchema = z
  .object({
    pickupInfo: z.discriminatedUnion("manualPickup", [
      customCotationManuelPickupFormSchema,
      customCotationPickupLocationIdFormSchema,
    ]),
    deliveryInfo: z.discriminatedUnion("singleDelivery", [
      customCotationSingleDeliveryFormSchema,
      customCotationMultipleDeliveryFormSchema,
    ]),
    billingEntityId: z
      .string({
        message: i18nMessage("shared:forms.customQuotationOther.billingEntityRequired"),
      })
      .min(1, i18nMessage("shared:forms.customQuotationOther.billingEntityRequired")),
    comment: z
      .string({
        required_error: i18nMessage("shared:forms.customQuotationOther.commentRequired"),
      })
      .optional(),
    insurance: z.boolean(),
    express: z.boolean(),
    reference: z.string().optional(),
    otherCustomQuotationType: z.nativeEnum(OtherCustomQuotationTypeEnum),
    newFilesId: z.array(z.string()).optional(), // created files ids that needs to be linked to the expedition
    removedFilesId: z.array(z.string()).optional(), // existing files ids that needs to be deleted
  })
  .superRefine((data, ctx) => {
    if (!data.deliveryInfo.singleDelivery) return;
    if (
      (data.deliveryInfo.countryCode === "CA" || data.deliveryInfo.countryCode === "US") &&
      (!data.deliveryInfo.stateOrProvinceCode || data.deliveryInfo.stateOrProvinceCode.length !== 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("validation:stateOrProvinceRequired"),
        path: ["deliveryInfo.stateOrProvinceCode"],
      });
    }
    if (
      countryRequiresPostalCode(data.deliveryInfo.countryCode) &&
      !data.deliveryInfo.deliveryPostalCode
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("validation:postalCodeRequiredForCountry"),
        path: ["deliveryInfo.deliveryPostalCode"],
      });
    }
  });

export type CustomCotationsOtherFormData = z.infer<typeof CustomCotationsOtherFormSchema>;
